import 'date-fns'
import { useTranslation } from 'react-i18next'
import React, { useEffect, useMemo, useState } from 'react'

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker'
import { makeStyles } from 'tss-react/mui'
import { getDate, getMonth } from 'date-fns'
import getDateLocale from 'ui/utils/getDateLocale'
import { jstNewDate } from 'ui/utils/jstDatetime'
import { TextField } from '@mui/material'
import theme from '../theme'

const useStyles = makeStyles()(() => ({
  input: { backgroundColor: theme.palette.background.paper },
  noMargin: {
    '& > input': {
      maxWidth: 116,
      textAlign: 'right',
    },
    '& > .MuiInputAdornment-root': {
      marginLeft: -8,
    },
  },
}))

export type DatePickerProps = {
  date: Date | null
  onDateChange: (date: Date, value?: string | null) => void
  format?: string
  label?: string
  minDate?: Date | null
  maxDate?: Date | null
  invalidDateMessage?: React.ReactNode
  maxDateMessage?: React.ReactNode
  minDateMessage?: React.ReactNode
  disableUnderline?: boolean
  reservableDates?: Date[]
  onChangeCalenderMonth?: (date: Date) => void
}

const DatePicker: React.FC<DatePickerProps> = ({
  date,
  onDateChange,
  format,
  disableUnderline,
  reservableDates,
  onChangeCalenderMonth,
  ...restProps
}: DatePickerProps) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()

  const [open, setOpen] = useState(false)
  const [currentMonth, setCurrentMonth] = useState<number>()

  useEffect(() => {
    setCurrentMonth(getMonth(date as Date))
  }, [date])

  // NOTE: 高速化のため日付リストをプリミティブに変換
  const reservableDatesString = reservableDates?.join('')
  const reservableDateNumbers: number[] | undefined = useMemo(() => {
    return reservableDates
      ?.filter((d) => getMonth(d as Date) === currentMonth)
      .map((d) => getDate(d as Date))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservableDatesString, currentMonth])

  const handleMonthChange = (date: Date | null) => {
    setCurrentMonth(getMonth(date as Date))
    onChangeCalenderMonth?.(date as Date)
  }

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={getDateLocale()}
    >
      <MuiDatePicker
        inputFormat={format || t('datetime.yyyy年MM月dd日')}
        value={date}
        onChange={(date) => {
          date && onDateChange(date)
        }}
        onMonthChange={handleMonthChange}
        {...restProps}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            InputProps={{
              ...params.InputProps,
              className: cx(
                classes.input,
                disableUnderline && classes.noMargin
              ),
              disableUnderline,
            }}
          />
        )}
        open={open}
        onAccept={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)} // NOTE: ポップオーバーの外側をクリックしたとき
        shouldDisableDate={(date: any) => {
          if (!reservableDateNumbers) {
            return false
          }
          return !reservableDateNumbers.includes(date?.getDate?.())
        }}
      />
    </LocalizationProvider>
  )
}

DatePicker.defaultProps = {
  date: jstNewDate,
}

export default DatePicker
